import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { User } from "@supabase/supabase-js";

// Shared, cached auth user. Reads the JWT from local storage via getSession()
// (no network round-trip) so it is instant on cold start. All components that
// call useAuthUser hit the same react-query cache instead of firing their own
// supabase.auth.getUser() network call.
export function useAuthUser() {
  const qc = useQueryClient();
  const query = useQuery<User | null>({
    queryKey: ["auth_user"],
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      return data.session?.user ?? null;
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      qc.setQueryData(["auth_user"], session?.user ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, [qc]);

  return { user: query.data ?? null, loading: query.isLoading };
}

export function useProfile() {
  const { user } = useAuthUser();
  return useQuery({
    queryKey: ["profile", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase.from("profiles").select("*").eq("id", user!.id).maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });
}

export function useIsAdmin() {
  const { user } = useAuthUser();
  return useQuery({
    queryKey: ["is_admin", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase.from("user_roles").select("role").eq("user_id", user!.id).in("role", ["admin", "sub_admin"]).maybeSingle();
      if (error) throw error;
      return !!data;
    },
    staleTime: 5 * 60_000,
  });
}
